import { CSSProperties, FC, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { useRouter } from "next/router";
import { Stock } from "@/models/stock";
import { useDemoMode, useIrThemeVars, withDemo } from "@/components/index-rebalance-v2/hooks";
import { IrHelpButton, UnlockButton, useIrTour } from "@/components/index-rebalance-v2/ui";
import TourGuide from "@/components/tour/TourGuide";
import { getDivStock } from "@/controllers/dividends-v2";
import {
    BorrowBox,
    ChanceBox,
    ComingSoonBox,
    CommentaryBox,
    CoverageBox,
    ExBehaviorBox,
    hasBorrow,
    hasChance,
    hasCommentary,
    hasCoverage,
    hasExBehavior,
    hasHistory,
    hasNext,
    hasOptionsImplied,
    hasPolicy,
    HistoryBox,
    LadderBox,
    ListsBox,
    NextDividendBox,
    OptionsBox,
    PeersBox,
    PolicyBox,
    Safe,
    TrackRecordBox,
    YieldBox,
} from "./Boxes";
import DividendAlertBox from "./DividendAlertBox";
import { EvolutionDrawer } from "./Evolution";
import { hasTrackRecord } from "./evolutionData";
import DividendsUnlockDialog from "../DividendsUnlockDialog";
import { publicationOf } from "../publication";
import { demoStockPayload, lockedPayload, realStockPayload, unpublishedPayload } from "./fixtures";
import { DivLadderRow, DivPeer, DivStockResponse } from "@/models/dividends-v2";
import styles from "./StockDividendTab.module.scss";

type LoadState = { loading: boolean; data: DivStockResponse | null; missing: boolean; error: string | null };

/** The stock payload: the fixture in demo mode, else `GET dividends/stock/<tradingitem_id>`. */
const useDividendStock = (stock: Stock): LoadState => {
    const router = useRouter();
    const demo = useDemoMode();
    const [state, setState] = useState<LoadState>({ loading: true, data: null, missing: false, error: null });
    const id = stock.tradingitem_id;
    const isFund = stock.type !== 1;
    // `?demo=1&example=walgreens` shows the Walgreens fixture (high cut chance, late) on any stock page;
    // `&example=locked` shows what a caller below Advanced gets; `&unpublished=1` what a customer gets
    // before the forecasts are published (TED #2467, combines with the others); `&example=real` the
    // payload production served a signed-out visitor for Apple on 2026-10-15 (unpublished and locked).
    const example = typeof router.query.example === "string" ? router.query.example : null;
    const unpublished = router.query.unpublished === "1";
    useEffect(() => {
        if (!router.isReady) return undefined;
        if (demo) {
            if (example === "real") {
                setState({ loading: false, data: realStockPayload(), missing: false, error: null });
                return undefined;
            }
            const fixture = demoStockPayload(example === "walgreens" ? "WBA" : stock.ticker, isFund);
            const gated = unpublished ? unpublishedPayload(fixture) : fixture;
            setState({ loading: false, data: example === "locked" ? lockedPayload(gated) : gated, missing: false, error: null });
            return undefined;
        }
        if (!id) {
            setState({ loading: false, data: null, missing: true, error: null });
            return undefined;
        }
        const ac = new AbortController();
        setState((s) => ({ ...s, loading: true, error: null }));
        getDivStock(id, ac.signal)
            .then((res) => {
                if (ac.signal.aborted) return;
                if (res.status === "ok" && res.data) setState({ loading: false, data: res.data, missing: false, error: null });
                else if (res.status === "missing") setState({ loading: false, data: null, missing: true, error: null });
                else if (res.status === "warming") setState({ loading: false, data: null, missing: false, error: "Preparing this stock's dividend data. Try again in a moment." });
                else setState({ loading: false, data: null, missing: false, error: "Dividend data could not be loaded. Try again in a moment." });
            })
            .catch((err) => {
                if (axios.isCancel(err) || ac.signal.aborted) return;
                setState({ loading: false, data: null, missing: false, error: "Dividend data could not be loaded. Try again in a moment." });
            });
        return () => ac.abort();
    }, [router.isReady, demo, id, stock.ticker, isFund, example, unpublished]);
    return state;
};

/**
 * From this width, and with this much height below the tab's top edge, the tab fills the window with
 * no page scroll. Less height would squeeze the ladder and the history to a few rows, so a shorter
 * window keeps the natural, scrolling layout.
 */
const FILL_MIN_WIDTH = 1500;
const FILL_MIN_HEIGHT = 720;

/**
 * Fill the window: from {@link FILL_MIN_WIDTH} wide with {@link FILL_MIN_HEIGHT} below its top edge,
 * the root's height is the window's height below that edge, and the grid's rows share it (a box with
 * more than its row holds scrolls inside itself). Otherwise the natural, scrolling layout.
 */
const useFillWindow = (deps: unknown[]) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = useState<number | null>(null);
    const measure = useCallback(() => {
        const el = ref.current;
        if (!el || typeof window === "undefined") return;
        const available = Math.floor(window.innerHeight - (el.getBoundingClientRect().top + window.scrollY));
        setHeight(window.innerWidth >= FILL_MIN_WIDTH && available >= FILL_MIN_HEIGHT ? available : null);
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(measure, [measure, ...deps]);
    useEffect(() => {
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [measure]);
    return { ref, height };
};

/** Spans that fill a twelve-column row with `n` boxes: the first `12 % n` get one column more. */
const rowSpans = (n: number): number[] => {
    if (n <= 0) return [];
    if (n > 6) return Array(n).fill(2);
    const base = Math.floor(12 / n);
    const extra = 12 - base * n;
    return Array.from({ length: n }, (_, i) => base + (i < extra ? 1 : 0));
};

const xl = (n: number) =>
    ({
        "--xl-span": n,
    }) as CSSProperties;

/**
 * The stock page's Dividend tab, built as the Index Rebalance trade page's twelve-column box grid
 * (design mockup `mock-stock.html`, epic ortex&131): three tiles (next dividend, chance of a change,
 * yield) and the commentary; the forecast ladder and the dividend history; then the small boxes
 * (coverage, stated policy, borrow around the record date, what options imply, ex-date behavior,
 * lists, peers, alert me). Every box comes from one block of the `stock/<tradingitem_id>` payload
 * and is dropped when that block is null, so a fund gets only the boxes it has data for.
 *
 * From 1500px the page sets each box's span so every row is full whatever boxes are present; below
 * that the grid's classes place them (dense flow fills any hole).
 */
const StockDividendTab: FC<{ stock: Stock }> = ({ stock }) => {
    const demo = useDemoMode();
    const themeVars = useIrThemeVars();
    const state = useDividendStock(stock);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [alertsUnavailable, setAlertsUnavailable] = useState(false);
    // The ladder row whose forecast path is open in the drawer (TED #2456).
    const [pathRow, setPathRow] = useState<DivLadderRow | null>(null);
    const p = state.data;
    const onUnlock = () => setUnlockOpen(true);
    // Whether this listing's forecasts are shown to this caller (staff: always). Unpublished, the
    // forecast-only boxes go and one line says the forecasts are coming (TED #2467).
    const forecasts = publicationOf(p).everywhere;
    // The page's guided tour (TED screening tour `stock_dividend`), from the Help button and once on a first visit.
    const tour = useIrTour("stock_dividend", !!p && !p.locked && !demo);
    const fill = useFillWindow([!!p, state.loading]);
    const ccy = p?.next?.currency ?? p?.currency ?? null;

    const tab = (page: string) => withDemo(`/s/${stock.exchangesymbol}/${stock.ticker}/${page}`, demo);
    const peerHref = (peer: DivPeer) => withDemo(`/s/${encodeURIComponent(peer.exchange_symbol ?? "")}/${encodeURIComponent(peer.ticker)}/dividend`, demo);

    let grid: ReactNode = null;
    if (p) {
        const tiles: { key: string; node: ReactNode }[] = [];
        if (hasNext(p)) tiles.push({ key: "next", node: <NextDividendBox payload={p} /> });
        if (forecasts && hasChance(p)) tiles.push({ key: "chance", node: <ChanceBox payload={p} /> });
        // Unpublished: the line saying forecasts are coming sits where the chance tile would be.
        if (!forecasts) tiles.push({ key: "soon", node: <ComingSoonBox /> });
        if (p.yield) tiles.push({ key: "yield", node: <YieldBox payload={p} forecasts={forecasts} /> });
        // The listing's track record: TED sends it only where its forecasts are published.
        if (forecasts && hasTrackRecord(p)) tiles.push({ key: "track", node: <TrackRecordBox payload={p} /> });
        const commentary = hasCommentary(p);
        const tileSpans = tiles.length ? rowSpans(tiles.length).map((s) => (commentary ? s / 2 : s)) : [];
        const tileSpanFixed = tileSpans.map((s) => Math.max(2, Math.floor(s)));
        const commentarySpan = 12 - tileSpanFixed.reduce((a, b) => a + b, 0) || 12;

        const ladder = (p.ladder?.length ?? 0) > 0;
        const history = hasHistory(p);
        const smalls: { key: string; node: ReactNode }[] = [];
        if (hasCoverage(p.coverage)) smalls.push({ key: "coverage", node: <CoverageBox coverage={p.coverage} forecasts={forecasts} /> });
        if (hasPolicy(p.policy)) smalls.push({ key: "policy", node: <PolicyBox policy={p.policy} /> });
        if (hasBorrow(p.borrow)) smalls.push({ key: "borrow", node: <BorrowBox borrow={p.borrow} href={tab("short-interest")} /> });
        if (hasOptionsImplied(p.options)) smalls.push({ key: "options", node: <OptionsBox options={p.options} href={tab("options")} /> });
        if (hasExBehavior(p.ex_behavior)) smalls.push({ key: "ex", node: <ExBehaviorBox ex={p.ex_behavior} /> });
        if (p.lists?.length) smalls.push({ key: "lists", node: <ListsBox lists={p.lists} href={stock.type === 1 ? tab("index-rebalance") : null} /> });
        // Peers compare next forecasts: none to compare before they are published.
        if (forecasts && p.peers?.length) smalls.push({ key: "peers", node: <PeersBox peers={p.peers} linkFor={peerHref} /> });
        if (!alertsUnavailable && p.tradingitem_id)
            smalls.push({
                key: "alert",
                node: <DividendAlertBox tradingitemId={p.tradingitem_id} ticker={p.ticker || stock.ticker} locked={p.locked} demo={demo} forecasts={forecasts} onUnlock={onUnlock} onUnavailable={() => setAlertsUnavailable(true)} />,
            });

        // Beside the ladder and the history (rows two and three): two small boxes stacked when both big
        // boxes are there; beside a single one, two stacked at a third of the row or four two by two.
        const bigCount = (ladder ? 1 : 0) + (history ? 1 : 0);
        const besideCount = Math.min(smalls.length, bigCount === 2 ? 2 : bigCount === 1 ? 4 : 0);
        const beside = smalls.slice(0, besideCount);
        const bottom = smalls.slice(besideCount);
        const bottomSpans = rowSpans(bottom.length);
        const besideSpan = bigCount === 1 && besideCount <= 2 ? 4 : 2;
        // The window-filling rows suit the usual page: tiles, then the ladder and history over two rows.
        const fillRows = bigCount > 0;
        const besideWidth = besideCount === 0 ? 0 : bigCount === 2 ? 2 : besideCount <= 2 ? 4 : 4;
        const ladderSpan = history ? (besideWidth ? 6 : 7) : 12 - besideWidth;
        const historySpan = ladder ? 12 - ladderSpan - besideWidth : 12 - besideWidth;
        grid = (
            <div className={`${styles.grid}${fill.height !== null && fillRows ? ` ${styles.gridFill}` : ""}`}>
                {tiles.map((t, i) => (
                    <div key={t.key} className={`${styles.tile} ${styles.xlSpan}`} style={xl(tileSpanFixed[i])}>
                        <Safe>{t.node}</Safe>
                    </div>
                ))}
                {commentary && (
                    <div className={`${styles.wide} ${styles.xlSpan}`} style={xl(commentarySpan)}>
                        <Safe>
                            <CommentaryBox payload={p} />
                        </Safe>
                    </div>
                )}
                {ladder && (
                    <div className={`${styles.wide} ${styles.rows2} ${styles.xlSpan}`} style={xl(ladderSpan)}>
                        <Safe>
                            <LadderBox payload={p} forecasts={forecasts} onOpen={forecasts ? setPathRow : undefined} />
                        </Safe>
                    </div>
                )}
                {history && (
                    <div className={`${styles.mid} ${styles.rows2} ${styles.xlSpan}`} style={xl(historySpan)}>
                        <Safe>
                            <HistoryBox payload={p} />
                        </Safe>
                    </div>
                )}
                {beside.map((b) => (
                    <div key={b.key} className={`${styles.small} ${styles.xlSpan}`} style={xl(besideSpan)}>
                        <Safe>{b.node}</Safe>
                    </div>
                ))}
                {bottom.map((b, i) => (
                    <div key={b.key} className={`${styles.small} ${styles.xlSpan}`} style={xl(bottomSpans[i])}>
                        <Safe>{b.node}</Safe>
                    </div>
                ))}
            </div>
        );
    }

    const filled = fill.height !== null && !!p;
    return (
        <div ref={fill.ref} className={`${styles.root}${filled ? ` ${styles.rootFill}` : ""}`} style={filled ? { ...themeVars, height: fill.height ?? undefined } : themeVars}>
            {p && (
                <div className={styles.toolbar}>
                    <span>
                        Dividend amounts are <b>per share</b>
                        {ccy ? ` in ${ccy}` : ""}; yields are in per cent. Hover over the ⓘ on any box for what it shows.
                    </span>
                    {tour.steps.length > 0 && <IrHelpButton onClick={tour.start} />}
                </div>
            )}
            {demo && (
                <div className={styles.banner} role="status">
                    EXAMPLE DATA
                    <span>Figures from the design brief, not today&apos;s forecast. Remove ?demo=1 from the address to see live data.</span>
                </div>
            )}
            {state.loading && !p && <div className={styles.empty}>Loading dividend data…</div>}
            {state.error && <div className={styles.empty}>{state.error}</div>}
            {state.missing && <div className={styles.empty}>Dividend data for {stock.ticker} is not available yet.</div>}
            {p && p.locked && (
                <div className={styles.upsell}>
                    <span>
                        {forecasts ? (
                            <>
                                <b>Dates are free.</b> Forecast amounts, the chance of a raise or a cut, and the boxes below them are part of ORTEX Advanced.
                            </>
                        ) : (
                            <>
                                <b>Dates are free.</b> Dividend amounts, the history chart, coverage and borrow around the record date are part of ORTEX Advanced.
                            </>
                        )}
                    </span>
                    <UnlockButton onClick={onUnlock} label="See plans" />
                </div>
            )}
            {p && !p.next && !p.ladder?.length && !p.history?.annual?.length && <div className={styles.empty}>{forecasts ? `No dividend is expected for ${stock.ticker}.` : `No dividend has been declared for ${stock.ticker}.`}</div>}
            {grid}
            <TourGuide {...tour.props} />
            {p && <EvolutionDrawer payload={p} row={pathRow} demo={demo} themeVars={themeVars} onClose={() => setPathRow(null)} onUnlock={onUnlock} />}
            <DividendsUnlockDialog open={unlockOpen} onClose={() => setUnlockOpen(false)} forecasts={forecasts} />
        </div>
    );
};

export default StockDividendTab;
